import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Dialog, DialogContent, TextField, Select, MenuItem, FormControlLabel, Switch, CircularProgress, Popover } from '@mui/material';
import { ChromePicker } from 'react-color';

import CloseIcon from '@mui/icons-material/CloseOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMoreOutlined';
import { TEAM_ICON_DEFAULT } from '../../../common/staticData';

import { SaveButton } from '../components/common';
import UploadButton from '../components/uploadButton';
import lang from '../../../assets/lang.json';
import GameService from '../../../services/game.service';
import '../coach_style.css';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 250
        }
    }
};

const ColorPickerField = ({ label, color, onChange }) => {
    const [anchorEl, setAnchorEl] = useState(null);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <Typography sx={{ fontSize: '12px', fontWeight: 500, color: '#1a1b1d' }}>{label}</Typography>
            <Box
                onClick={(e) => setAnchorEl(e.currentTarget)}
                sx={{
                    width: '100%',
                    height: '40px',
                    borderRadius: '8px',
                    border: '1px solid #ccc',
                    backgroundColor: color || '#ffffff',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    '&:hover': { borderColor: '#999' }
                }}
            >
                <Typography sx={{ fontSize: '11px', color: '#666', textShadow: '0 0 2px white' }}>
                    Click to change
                </Typography>
            </Box>
            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
                <ChromePicker
                    color={color || '#ffffff'}
                    onChange={(c) => onChange(`rgba(${c.rgb.r}, ${c.rgb.g}, ${c.rgb.b}, ${c.rgb.a})`)}
                />
            </Popover>
        </Box>
    );
};

const TeamEditDialog = ({ open, onClose, team, t, onUpdateTeam }) => {
    const navigate = useNavigate();
    const [values, setValues] = useState({
        id: 0,
        name: '',
        short_name: '',
        image: '',
        team_color: '',
        second_color: '',
        sponsor_logo: '',
        sponsor_url: '',
        show_sponsor: false,
        create_highlights: false,
        team_language: 'en',
        filter_by_position: false
    });
    const [loading, setLoading] = useState(false);

    const handleChange = (prop) => (e) => {
        setValues({ ...values, [prop]: e.target.value });
    };

    const saveChanges = () => {
        setLoading(true);
        GameService.updateTeam(values)
            .then((res) => {
                setLoading(false);
                onUpdateTeam(values);
            })
            .catch((e) => {
                setLoading(false);
            });
        onClose();
        navigate('/new_coach/teams/');
    };

    useEffect(() => {
        setValues({
            ...values,
            id: team.team_id,
            name: team.team_name,
            short_name: team.team_short_name,
            image: team.team_image ?? '',
            team_color: team.team_color ?? 'white',
            second_color: team.team_second_color ?? 'white',
            sponsor_logo: team.team_sponsor_logo ?? '',
            sponsor_url: team.team_sponsor_url ?? '',
            show_sponsor: team.team_show_sponsor ?? false,
            create_highlights: team.team_create_highlights ?? false,
            team_language: team.team_language ?? 'en',
            filter_by_position: team.team_filter_by_position ?? false
        });
    }, [team]);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { maxHeight: '90vh', overflow: 'hidden' } }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 24px', borderBottom: '1px solid #eee' }}>
                <Typography sx={{ fontSize: '18px', fontWeight: 600, color: '#1a1b1d' }}>{t('Edit Team')}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }} onClick={onClose}>
                    <CloseIcon sx={{ width: '18px', height: '18px' }} />
                    <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#1a1b1d' }}>{t('Close')}</Typography>
                </Box>
            </Box>

            <DialogContent sx={{ padding: '24px', overflow: 'hidden' }}>
                {loading && (
                    <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(255,255,255,0.8)', zIndex: 10 }}>
                        <CircularProgress />
                    </Box>
                )}

                <Box sx={{ display: 'flex', gap: '32px' }}>
                    {/* Left column - Logos */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', width: '140px', flexShrink: 0 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                            <Typography sx={{ fontSize: '12px', fontWeight: 500, color: '#1a1b1d' }}>{t('Team Logo')}</Typography>
                            <UploadButton
                                class_name="upload-team-view"
                                id_name="team-logo"
                                dirName={process.env.REACT_APP_DIR_TEAM}
                                img={values.image ? (values.image.includes(process.env.REACT_APP_S3_URI) ? values.image : process.env.REACT_APP_S3_URI + values.image) : TEAM_ICON_DEFAULT}
                                onURL={(url) => setValues({ ...values, image: url })}
                            />
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                            <Typography sx={{ fontSize: '12px', fontWeight: 500, color: '#1a1b1d' }}>{t('Sponsor Logo')}</Typography>
                            <UploadButton
                                class_name="upload-sponsor-view"
                                id_name="sponsor-logo"
                                dirName={process.env.REACT_APP_DIR_TEAM}
                                img={values.sponsor_logo ? (values.sponsor_logo.includes(process.env.REACT_APP_S3_URI) ? values.sponsor_logo : process.env.REACT_APP_S3_URI + values.sponsor_logo) : TEAM_ICON_DEFAULT}
                                onURL={(url) => setValues({ ...values, sponsor_logo: url })}
                            />
                        </Box>
                    </Box>

                    {/* Right column - Form fields */}
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        {/* Row 1: Team Name and Short Name */}
                        <Box sx={{ display: 'flex', gap: '16px' }}>
                            <Box sx={{ flex: 1 }}>
                                <Typography sx={{ fontSize: '12px', fontWeight: 500, color: '#1a1b1d', marginBottom: '4px' }}>{t('Team Name')}</Typography>
                                <TextField
                                    value={values.name}
                                    onChange={handleChange('name')}
                                    placeholder={t('Team Name')}
                                    fullWidth
                                    size="small"
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                                />
                            </Box>
                            <Box sx={{ flex: 1 }}>
                                <Typography sx={{ fontSize: '12px', fontWeight: 500, color: '#1a1b1d', marginBottom: '4px' }}>{t('Team Short Name')}</Typography>
                                <TextField
                                    value={values.short_name}
                                    onChange={handleChange('short_name')}
                                    placeholder={t('Team Short Name')}
                                    fullWidth
                                    size="small"
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                                />
                            </Box>
                        </Box>

                        {/* Row 2: Colors */}
                        <Box sx={{ display: 'flex', gap: '16px' }}>
                            <Box sx={{ flex: 1 }}>
                                <ColorPickerField
                                    label={t('Team Color')}
                                    color={values.team_color}
                                    onChange={(color) => setValues({ ...values, team_color: color })}
                                />
                            </Box>
                            <Box sx={{ flex: 1 }}>
                                <ColorPickerField
                                    label={t('Second Color')}
                                    color={values.second_color}
                                    onChange={(color) => setValues({ ...values, second_color: color })}
                                />
                            </Box>
                        </Box>

                        {/* Row 3: Sponsor URL and Language */}
                        <Box sx={{ display: 'flex', gap: '16px' }}>
                            <Box sx={{ flex: 1 }}>
                                <Typography sx={{ fontSize: '12px', fontWeight: 500, color: '#1a1b1d', marginBottom: '4px' }}>{t('Sponsor URL')}</Typography>
                                <TextField
                                    value={values.sponsor_url}
                                    onChange={handleChange('sponsor_url')}
                                    placeholder={t('Sponsor URL')}
                                    fullWidth
                                    size="small"
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                                />
                            </Box>
                            <Box sx={{ flex: 1 }}>
                                <Typography sx={{ fontSize: '12px', fontWeight: 500, color: '#1a1b1d', marginBottom: '4px' }}>{t('Team Language')}</Typography>
                                <Select
                                    value={values.team_language}
                                    onChange={handleChange('team_language')}
                                    IconComponent={ExpandMoreIcon}
                                    fullWidth
                                    size="small"
                                    MenuProps={MenuProps}
                                    sx={{ borderRadius: '8px' }}
                                >
                                    {lang.map((item, index) => (
                                        <MenuItem key={index} value={item.code}>
                                            {item.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </Box>
                        </Box>

                        {/* Row 4: Toggle */}
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={values.show_sponsor}
                                        onChange={() => setValues({ ...values, show_sponsor: !values.show_sponsor })}
                                        size="small"
                                    />
                                }
                                label={<Typography sx={{ fontSize: '13px' }}>{t('Show Sponsor')}</Typography>}
                            />
                        </Box>
                    </Box>
                </Box>

                {/* Save Button */}
                <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: '24px' }}>
                    <SaveButton onClick={saveChanges} sx={{ width: '200px', fontSize: '14px' }}>
                        {t('Save Changes')}
                    </SaveButton>
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default TeamEditDialog;
